import { z } from 'zod';
import { uuid, isoTimestamp } from '../schemas/common.js';
/**
 * A todo's little activity log (TODOS.md §3).
 *
 * Each entry records one thing that happened to a todo — **what**, **by whom**,
 * and **when** — so the sheet can show a short history ("Aangemaakt door Sanne",
 * "Afgevinkt", "Bewerkt: categorie"). It is a jsonb array on the row (migration
 * 032), not its own table: the entries are tiny and only ever read for the one
 * todo they belong to.
 *
 * The log rides the ordinary per-row LWW sync, so a log entry is only as durable
 * as the edit it accompanies — two phones editing the same todo at once keep one
 * row and one entry, exactly as they keep one edit. For a two-person checklist
 * that is the accepted trade the whole module already makes.
 */
/**
 * The kinds of thing that happen to a todo.
 *  - `created`   — added to the list
 *  - `completed` — ticked off (a non-recurring todo, or a recurring one stopped)
 *  - `reopened`  — un-ticked
 *  - `recurred`  — a recurring todo completed and rolled forward to its next due
 *  - `edited`    — a field changed; `detail` names which (title / who / …)
 */
export const TODO_LOG_ACTIONS = ['created', 'completed', 'reopened', 'recurred', 'edited'];
export const todoLogEntry = z.object({
    /** When it happened (wall clock, for display — not the LWW `updated_at`). */
    at: isoTimestamp,
    /** Who did it, or null when the actor is unknown (e.g. a system roll-forward). */
    by: uuid.nullable(),
    action: z.enum(TODO_LOG_ACTIONS),
    /** For `edited`, a short human note of what changed ("titel, categorie"). */
    detail: z.string().max(200).nullish(),
});
/** Keep a todo's history bounded — the last N events is plenty for a checklist. */
export const TODO_LOG_MAX = 50;
/** Build one log entry, stamping it with the current wall clock. */
export function logEntry(action, by, detail) {
    return { at: new Date().toISOString(), by, action, detail: detail ?? null };
}
/**
 * Append an entry to a todo's log, oldest first, trimmed to `TODO_LOG_MAX`.
 * `existing` may be null/undefined (a todo with no history yet).
 */
export function appendLog(existing, entry) {
    const next = [...(existing ?? []), entry];
    return next.length > TODO_LOG_MAX ? next.slice(next.length - TODO_LOG_MAX) : next;
}
//# sourceMappingURL=log.js.map